import axios from 'axios';
import { getApp, getApps, initializeApp } from 'firebase/app';
import {
	getMessaging,
	getToken,
	isSupported,
	deleteToken,
} from 'firebase/messaging';
import { toast } from 'sonner';

import {
	PUSH_TOKEN_KEY,
	isSavedResponse,
	readOptIn,
	testNotificationErrorDescription,
	writeOptIn,
} from '../utils/desktopNotifications';

import { SET_BROWSER_NOTIFICATION_PERMISSION_STATUS } from './types';

// Keep in sync with client/public/firebase-messaging-sw.js
const firebaseConfig = import.meta.env.PROD
	? {
			apiKey: 'AIzaSyBCE5th3PvFuSgUy2b_Ek0oo2YkaFuvxFs',
			authDomain: 'desktop-notifications-494ac.firebaseapp.com',
			projectId: 'desktop-notifications-494ac',
			storageBucket: 'desktop-notifications-494ac.appspot.com',
			messagingSenderId: '663907233156',
			appId: '1:663907233156:web:2ca8ef3ef3b4fc01cc01d1',
			measurementId: 'G-G2PE2MG819',
	  }
	: {
			apiKey: 'AIzaSyBpbyUnEuKfaycMRq6Bp0WD-6lPCQHwqPA',
			authDomain: 'push-notifications-test-e11ab.firebaseapp.com',
			projectId: 'push-notifications-test-e11ab',
			storageBucket: 'push-notifications-test-e11ab.appspot.com',
			messagingSenderId: '1031876986727',
			appId: '1:1031876986727:web:87905d687549a0da9611c2',
			measurementId: 'G-3TVMT82M54',
	  };

const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

const DELETE_TOKEN_URL = '/api/delete-firebase-push-token';

// logout waits at most this long for the token to be released
const LOGOUT_RELEASE_TIMEOUT_MS = 2000;

const getStorage = () => {
	try {
		return window.localStorage;
	} catch {
		return undefined;
	}
};

const getStoredToken = () => {
	try {
		return getStorage()?.getItem(PUSH_TOKEN_KEY) || null;
	} catch {
		return null;
	}
};

const setStoredToken = (token) => {
	try {
		getStorage()?.setItem(PUSH_TOKEN_KEY, token);
	} catch (err) {
		console.log(err);
	}
};

const clearStoredToken = () => {
	try {
		getStorage()?.removeItem(PUSH_TOKEN_KEY);
	} catch (err) {
		console.log(err);
	}
};

const getUserId = (getState) => getState?.()?.auth?.user?._id;

const setStatus = (dispatch, status) =>
	dispatch({
		type: SET_BROWSER_NOTIFICATION_PERMISSION_STATUS,
		payload: status,
	});

const hasPushApis = () =>
	typeof window !== 'undefined' &&
	'Notification' in window &&
	'PushManager' in window &&
	typeof navigator !== 'undefined' &&
	'serviceWorker' in navigator;

const isBrowserSupported = async () => {
	if (!hasPushApis()) {
		return false;
	}

	try {
		return await isSupported();
	} catch {
		return false;
	}
};

// Firebase is only started when notifications are used. getToken registers
// the service worker and waits for it to be active, so no delay is needed.
let messagingPromise;

const getMessagingInstance = () => {
	if (!messagingPromise) {
		messagingPromise = (async () => {
			if (!(await isBrowserSupported())) {
				return null;
			}

			const firebaseApp = getApps().length
				? getApp()
				: initializeApp(firebaseConfig);

			return getMessaging(firebaseApp);
		})().catch((err) => {
			console.log(err);
			messagingPromise = undefined;
			return null;
		});
	}

	return messagingPromise;
};

const withTimeout = (promise, ms) =>
	Promise.race([
		promise,
		new Promise((resolve) => {
			setTimeout(resolve, ms);
		}),
	]);

// The delete route needs no session, and keepalive lets the request finish
// while the page navigates away (logout, session expiry).
const deleteTokenOnServer = (token) => {
	const body = JSON.stringify({ token });

	try {
		return fetch(DELETE_TOKEN_URL, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
			keepalive: true,
			credentials: 'same-origin',
		}).catch((err) => console.log('Error deleting push token', err));
	} catch {
		try {
			navigator.sendBeacon?.(
				DELETE_TOKEN_URL,
				new Blob([body], { type: 'application/json' })
			);
		} catch (beaconErr) {
			console.log('Error deleting push token', beaconErr);
		}
		return Promise.resolve();
	}
};

const toastError = (title, description) => toast.error(title, { description });

const getErrorDescription = (err, fallback) =>
	err?.response?.data?.message ||
	(typeof err?.response?.data?.error === 'string' &&
		err.response.data.error) ||
	err?.message ||
	fallback;

// Pages hear about pushes from the service worker, which shows every
// notification itself. Mentions refresh their badges from here.
let isListeningForPushMessages = false;

export const listenForPushMessages = () => {
	if (isListeningForPushMessages || !hasPushApis()) {
		return;
	}

	isListeningForPushMessages = true;

	navigator.serviceWorker.addEventListener('message', (event) => {
		if (event.data?.type !== 'CHEKKIT_PUSH_RECEIVED') {
			return;
		}

		const type = event.data.data?.type;

		if (type === 'team_mention') {
			window.dispatchEvent(new Event('chekkit-team-mentions-changed'));
		}

		if (type === 'note_mention') {
			window.dispatchEvent(new Event('chekkit-mentions-changed'));
		}
	});
};

/**
 * Gets this browser's FCM token and registers it for the signed-in user.
 * localStorage only records the token after the server confirmed it, so the
 * toggle never shows on for a browser the server doesn't know about.
 * Throws on any failure.
 */
const saveToken = async () => {
	const messagingInstance = await getMessagingInstance();

	if (!messagingInstance) {
		throw new Error("This browser doesn't support desktop notifications.");
	}

	const token = await getToken(messagingInstance, { vapidKey });

	if (!token) {
		throw new Error('The browser did not return a notification token.');
	}

	const response = await axios.post(
		'/api/save-firebase-push-token',
		{ token },
		{ _suppressErrorToast: true }
	);

	if (!isSavedResponse(response.status, response.data)) {
		throw new Error('The server could not save this browser.');
	}

	const previousToken = getStoredToken();

	setStoredToken(token);

	// the browser rotated its token, so the old one will never work again
	if (previousToken && previousToken !== token) {
		deleteTokenOnServer(previousToken);
	}

	return token;
};

// quiet is for the page-load heartbeat: a failure there keeps the toggle as
// it was (the server most likely still has the token) and shows no toast.
const saveTokenOrReport = async (dispatch, { quiet = false } = {}) => {
	try {
		const token = await saveToken();
		setStatus(dispatch, 'granted');
		return token;
	} catch (err) {
		console.log('saveToken error', err);
		setStatus(dispatch, quiet && getStoredToken() ? 'granted' : 'disabled');

		// signing in again is handled by the 401 redirect
		if (!quiet && err?.response?.status !== 401) {
			toastError(
				'Unable to save browser notifications',
				getErrorDescription(err, 'Please try enabling notifications again.')
			);
		}
		return null;
	}
};

// delete the token on the server, then in FCM, then locally
const removeToken = async () => {
	const token = getStoredToken();

	if (!token) {
		return;
	}

	await deleteTokenOnServer(token);

	try {
		const messagingInstance = await getMessagingInstance();

		if (messagingInstance) {
			await deleteToken(messagingInstance);
		}
	} catch (err) {
		console.log('removeToken error', err);
	}

	clearStoredToken();
};

/**
 * Best-effort release for pages that are about to navigate away and can't
 * wait (session expiry). The server row is deleted with a keepalive request;
 * the FCM subscription is left for the next sign-in to reuse.
 */
export const releaseBrowserPushToken = () => {
	const token = getStoredToken();

	if (!token) {
		return;
	}

	clearStoredToken();
	deleteTokenOnServer(token);
};

/**
 * Asks the server to push a test notification to this browser.
 * Returns true when it was sent.
 */
export const sendTestBrowserNotification = async () => {
	const token = getStoredToken();

	if (!token) {
		toastError(
			"Couldn't send a test notification",
			testNotificationErrorDescription('token-not-registered')
		);
		return false;
	}

	try {
		const { data } = await axios.post(
			'/api/browser-notifications/test',
			{ token },
			{ _suppressErrorToast: true, timeout: 20000 }
		);

		if (data?.error !== false) {
			throw Object.assign(new Error('send-failed'), { code: data?.code });
		}

		toast.success('Test notification sent', {
			description:
				"You should see it now. If not, check your browser's and your computer's notification settings for this site.",
		});
		return true;
	} catch (err) {
		if (err?.response?.status === 401) {
			return false;
		}

		// axios sets err.code (ERR_NETWORK, ECONNABORTED) when there was no reply
		const code = err?.response
			? err.response.data?.code || 'send-failed'
			: err?.code && !err?.isAxiosError
				? err.code
				: 'network-error';

		toastError(
			"Couldn't send a test notification",
			testNotificationErrorDescription(code)
		);
		return false;
	}
};

// Called from the toggle click, so the permission prompt has a user gesture.
const enableBrowserNotifications = async (dispatch, getState) => {
	if (!hasPushApis()) {
		return setStatus(dispatch, 'unsupported');
	}

	// Ask before any await: Safari only shows the prompt during the click.
	const permissionRequest =
		Notification.permission === 'granted'
			? Promise.resolve('granted')
			: Notification.requestPermission();

	const [permission, supported] = await Promise.all([
		permissionRequest,
		isBrowserSupported(),
	]);

	if (!supported) {
		return setStatus(dispatch, 'unsupported');
	}

	if (permission === 'denied') {
		return setStatus(dispatch, 'denied');
	}

	if (permission !== 'granted') {
		// the prompt was dismissed
		return setStatus(dispatch, 'disabled');
	}

	const token = await saveTokenOrReport(dispatch);

	if (!token) {
		return;
	}

	writeOptIn(getStorage(), getUserId(getState), true);

	await sendTestBrowserNotification();
};

export const handleDisableNotificationsOnLogout = () => async (dispatch) => {
	await withTimeout(
		removeToken().catch((err) => console.log('removeToken error', err)),
		LOGOUT_RELEASE_TIMEOUT_MS
	);

	setStatus(dispatch, 'disabled');
};

export const handleToggleBrowserNotifications =
	({ shouldEnable }) =>
	async (dispatch, getState) => {
		try {
			if (shouldEnable) {
				return await enableBrowserNotifications(dispatch, getState);
			}

			writeOptIn(getStorage(), getUserId(getState), false);

			await removeToken();

			setStatus(dispatch, 'disabled');

			toast.success('Disabled browser notifications');
		} catch (err) {
			console.log('Error in handleToggleBrowserNotifications: ', err);
		}
	};

/**
 * Runs on every dashboard load. It never prompts (there is no user gesture);
 * it reports the browser's state and, while this user has notifications on,
 * re-saves the token. That refreshes lastSeenAt so the server's 60-day TTL
 * never removes a browser that is still used.
 */
// The dashboard re-runs setup whenever auth changes; one heartbeat per
// user and location every few minutes is plenty.
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
let lastHeartbeat = { key: null, at: 0 };

export const setUpNotifications = () => async (dispatch, getState) => {
	try {
		if (!(await isBrowserSupported())) {
			return setStatus(dispatch, 'unsupported');
		}

		const { permission } = Notification;
		const auth = getState?.()?.auth;
		const isOn =
			permission === 'granted' &&
			readOptIn(getStorage(), getUserId(getState));

		if (!isOn) {
			// A token left from someone else's session, or from before the
			// browser permission was revoked, must not keep receiving pushes.
			releaseBrowserPushToken();

			return setStatus(
				dispatch,
				permission === 'denied' ? 'denied' : 'disabled'
			);
		}

		const heartbeatKey = `${auth?.user?._id}:${auth?._id}`;

		if (
			getStoredToken() &&
			lastHeartbeat.key === heartbeatKey &&
			Date.now() - lastHeartbeat.at < HEARTBEAT_INTERVAL_MS
		) {
			return setStatus(dispatch, 'granted');
		}

		const token = await saveTokenOrReport(dispatch, { quiet: true });

		lastHeartbeat = token
			? { key: heartbeatKey, at: Date.now() }
			: { key: null, at: 0 };
	} catch (err) {
		console.log('Error setting up notifications', err);
	}
};
